import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconButton } from '../../components/ui'
import { useAppSelector } from '../../store/hooks'
import { cn } from '../../utils/cn'
import { canEnter, STEP_IDS, STEP_TITLES, stepIndex, type StepId } from './wizardSteps'

interface WizardHeaderProps {
  step: StepId
  vendorName: string
  onBack?: () => void
}

/**
 * The wizard's brand anchor and progress.
 *
 * Progress is a numbered track rather than a bar: six nodes joined by a rule,
 * the last one a check for the confirmation. It is the same on every step, so
 * the customer can always see how far in they are and how much is left — and
 * every step they have already satisfied is a button, because the fastest way
 * back to "actually, a different time" is tapping the 4.
 */
export function WizardHeader({ step, vendorName, onBack }: WizardHeaderProps) {
  const index = stepIndex(step)
  const total = STEP_IDS.length

  return (
    <header className="sticky top-0 z-40 border-b border-outline-variant bg-surface">
      <div className="container-page flex items-center gap-3 py-4">
        <div className="w-10">
          {onBack && <IconButton icon="arrow_back" label="Back" onClick={onBack} />}
        </div>
        <p className="min-w-0 flex-1 truncate text-center font-display text-headline-sm tracking-tighter text-primary">
          {vendorName}
        </p>
        <div className="w-10" aria-hidden="true" />
      </div>

      <div className="container-page pb-5">
        <p className="mb-3 text-center font-label-caps text-label-caps uppercase text-on-surface-variant">
          Step {index + 1} of {total}
        </p>
        <StepTrack current={step} />
      </div>
    </header>
  )
}

function StepTrack({ current }: { current: StepId }) {
  const navigate = useNavigate()
  const selection = useAppSelector((s) => s.selection)
  const index = stepIndex(current)

  return (
    <ol className="mx-auto flex max-w-xl items-center" aria-label="Booking progress">
      {STEP_IDS.map((id, i) => {
        const isLast = i === STEP_IDS.length - 1
        // The confirmation is only ever reached by booking, so it stays inert
        // even though every other satisfied step is reachable from here.
        const reachable = !isLast && i !== index && canEnter(id, selection)

        return (
          <Fragment key={id}>
            {i > 0 && (
              <li
                aria-hidden="true"
                className={cn('h-px flex-1', i <= index ? 'bg-primary' : 'bg-outline-variant')}
              />
            )}
            <li>
              <StepNode
                label={STEP_TITLES[id]}
                position={i + 1}
                showCheck={isLast}
                state={i < index ? 'done' : i === index ? 'current' : 'upcoming'}
                onClick={reachable ? () => navigate(`../${id}`) : undefined}
              />
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}

interface StepNodeProps {
  label: string
  position: number
  showCheck: boolean
  state: 'done' | 'current' | 'upcoming'
  onClick?: () => void
}

function StepNode({ label, position, showCheck, state, onClick }: StepNodeProps) {
  const body = showCheck ? (
    <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
      check
    </span>
  ) : (
    position
  )

  const shape = cn(
    'flex h-8 w-8 items-center justify-center rounded-full font-label-caps text-label-caps transition-colors duration-300',
    state === 'upcoming'
      ? 'bg-surface-container-highest text-on-surface-variant'
      : 'bg-primary text-on-primary',
  )

  if (!onClick) {
    return (
      <span className={shape} aria-current={state === 'current' ? 'step' : undefined}>
        <span className="sr-only">{`Step ${position}: ${label}`}</span>
        {body}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(shape, 'hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary')}
    >
      <span className="sr-only">{`Go back to step ${position}: ${label}`}</span>
      {body}
    </button>
  )
}
